import { useProductFormSuspenseQuery } from "@@/backoffice/for-providers/products/shared/advanced/product-forms/use-product-form";
import { useCartContext } from "@@/carts/multi-carts/cart.context";
import { useWizardState } from "@@/pages/wizard-context";
import {
    ProductContextProvider,
    useProductFromContext,
} from "@@/products/state/queries/product/product.context";
import { useProductSuspenseQuery } from "@@/products/state/queries/product/use-product";
import { SuspenseWithErrorBoundary } from "@@/routes/shared/suspense-with-error-boundary";
import { browserLogger } from "@@/settings";
import { FadeAndHeight } from "@@/shared/animations/fade-and-height";
import { Conditional } from "@@/shared/conditional";
import {
    ConditionalPrePostRenderer,
    usePrePostRenderer,
} from "@@/shared/conditional-pre-post-renderer";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { HorizontalSectionDivider } from "@@/shared/dividers/horizontal-section-divider";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { ForEach } from "@@/shared/for-each";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { FormIdProvider } from "@@/shared/form/form-id.context";
import { Form3 } from "@@/shared/form/form3.form";
import { useForm3 } from "@@/shared/form/form3.use-form";
import { TextShimmer } from "@@/shared/pictures/shimmers";
import { NumberTextBox, TextBox } from "@@/shared/text";
import { FieldTitle } from "@@/shared/text/field-title";
import { useMountEffect } from "@@/shared/use-mount-effect";
import {
    useIsTranslatableEmpty,
    useIsTranslatableNotEmpty,
} from "@@/translations/use-translate";
import { useTheme } from "@emotion/react";
import type {
    Cart_FieldAnswer,
    Cart_ProductFormAnswer,
    Cart_ProductFormAnswerWorkingCopy,
    Cart_ProductFormUnfilledFieldAnswer,
    OrderItem,
    ProductForm,
    ProductFormField,
    ProductFormField_Checkbox,
    ProductFormField_CheckboxAnswer,
    ProductFormField_Select,
    ProductFormField_SelectAnswer,
    ProductFormField_Text,
    ProductFormField_TextAnswer,
    ProductFormFieldAnswer,
    ProductFormFieldId,
    ProductFormFieldQuantity,
    ProductFormId,
    ProductId,
    RequiredTranslatable,
    Translatable,
    TranslatableValueFactoryParams,
} from "@towni/common";
import {
    accommodationQuantityTypeMeta,
    asArray,
    assertNever,
    calculateProductFormFieldQuantity,
    cart__productFormAnswerWorkingCopyZodObject,
    cart__productFormAnswerZodObject,
    exists,
    getBookableResourceReferences,
    hasProp,
    isAccommodationQuantityType,
    isBookable,
    isBookableOptionId,
    isProductFormFieldCheckbox,
    isProductFormFieldSelect,
    isProductFormFieldText,
    joinTranslatables,
    maxOf,
    nowIsoAndUnix,
    orderUtils,
    productFormAnswerIdFactory,
    productFormCheckboxAnswerFactory,
    productFormCheckboxAnswerZodObject,
    productFormFieldUtils,
    productFormSelectAnswerFactory,
    productFormSelectAnswerZodObject,
    productFormTextAnswerFactory,
    productFormTextAnswerZodObject,
    repeatIndex,
    textUtils,
    translation,
} from "@towni/common";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useTransition,
} from "react";
import { reduce } from "remeda";
import {
    FormValidContext,
    useFormValidFromContext,
} from "./form-valid-context";
import { useRegisterProductFormInCart } from "./use-register-product-form";

type Props = {
    readonly className?: string;
    /** defaults to horizontal section divider */
    readonly divider?: React.JSX.Element;
    readonly beginWith?: React.JSX.Element;
    readonly endWith?: React.JSX.Element;
};

const ProductForms_Cart = (props: Props) => {
    const context = useCartContext(context => ({
        productIds: orderUtils.getProductIds(context.cart.orderItems),
        registerProductForm: context.registerProductForm,
        registerProductFormAnswers: context.registerProductFormAnswers,
        unregisterProductForm: context.unregisterProductForm,
    }));

    return (
        <SuspenseWithErrorBoundary
            fallback={
                <FlexColumn
                    fillParentWidth
                    css={{
                        minHeight: "fit-content",
                    }}>
                    <TextShimmer
                        animateHeight
                        rows={3}
                        css={{
                            flexShrink: 0,
                            padding: 20,
                        }}
                    />
                </FlexColumn>
            }>
            <FlexColumn className={props.className}>
                <ForEach
                    itemOf={context.productIds}
                    getKey={productId => productId}>
                    {(productId, index) => (
                        <ConditionalPrePostRenderer
                            preItem={<HorizontalSectionDivider />}
                            postItem={
                                index === context.productIds.length - 1 ? (
                                    <HorizontalSectionDivider />
                                ) : null
                            }>
                            <FadeAndHeight show>
                                <ProductForm_Cart
                                    key={productId}
                                    productId={productId}
                                    registerProductForm={
                                        context.registerProductForm
                                    }
                                    registerProductFormAnswers={
                                        context.registerProductFormAnswers
                                    }
                                />
                            </FadeAndHeight>
                        </ConditionalPrePostRenderer>
                    )}
                </ForEach>
            </FlexColumn>
        </SuspenseWithErrorBoundary>
    );
};

const getGroupName = (quantityGroup: ProductFormFieldQuantity) => {
    return typeof quantityGroup === "number" ? "NO_GROUP" : quantityGroup;
};

const ProductForm_Cart = (props: {
    productId: ProductId;
    registerProductForm: (form: ProductForm) => void;
    registerProductFormAnswers: (params: {
        productFormId: ProductFormId;
        result: Cart_ProductFormAnswer | null;
        errorMessage: Translatable | TranslatableValueFactoryParams;
    }) => void;
}) => {
    // BAD UGLY QUICK FIX
    const state = useWizardState({ doNotThrow: true });
    const setProductFormsPending = useCartContext(
        context => context.setProductFormsPending,
    );
    useMountEffect(() => {
        // When this function is mounted, we now that we are not
        // waiting any more for product forms to be fetched
        setProductFormsPending(false);
    });
    const productFormQuery = useProductFormSuspenseQuery(props.productId);
    const productForm = productFormQuery.data;

    usePrePostRenderer(!!productForm.fields.length);
    if (!productForm.fields.length) return null;

    return (
        <_ProductForm_Cart
            key={state?.index}
            {...props}
            productForm={productForm}
        />
    );
};

const _ProductForm_Cart = (props: {
    productId: ProductId;
    productForm: ProductForm;
    registerProductForm: (form: ProductForm) => void;
    registerProductFormAnswers: (params: {
        productFormId: ProductFormId;
        result: Cart_ProductFormAnswer | null;
        errorMessage: Translatable | TranslatableValueFactoryParams;
    }) => void;
}) => {
    const theme = useTheme();
    const clearProductFormAnswers = useCartContext(
        context => context.clearProductFormAnswers,
    );
    const productForm = props.productForm;
    const productQuery = useProductSuspenseQuery(props.productId);
    const hasForm = !!productForm.fields.length;
    const product = productQuery.data;
    const [, startTransition] = useTransition();
    const [valid, setValid] = useState(false);
    const orderItems = useCartContext(context => context.cart.orderItems);
    const workingCopyRef = useRef<Partial<Cart_ProductFormAnswerWorkingCopy>>({
        _id: productFormAnswerIdFactory(),
        _type: "CART__PRODUCT_FORM_ANSWER",
        _createdAt: nowIsoAndUnix(),
        providerId: productForm.providerId,
        productFormId: productForm._id,
        productId: productForm.productId,
        answers: productForm.fields.map(field => {
            if (isProductFormFieldCheckbox(field)) {
                // Set default value of checkbox field
                if (field.defaultValue === true) {
                    const quantity = calculateProductFormFieldQuantity(
                        field,
                        orderItems,
                    );
                    return productFormCheckboxAnswerFactory({
                        _id: field._id,
                        title: field.title,
                        required: quantity > 0 && field.required,
                        quantity,
                        groupedBy: getGroupName(field.quantity),
                        values: {
                            ...reduce(
                                repeatIndex(quantity),
                                (acc, _, index) => ({
                                    ...acc,
                                    [index]: !!field.defaultValue,
                                }),
                                {},
                            ),
                        },
                    });
                }
            }
            if (isProductFormFieldText(field)) {
                if (field.defaultValue) {
                    // Set default value of text field
                    const quantity = calculateProductFormFieldQuantity(
                        field,
                        orderItems,
                    );
                    return productFormTextAnswerFactory({
                        _id: field._id,
                        groupedBy: getGroupName(field.quantity),
                        quantity,
                        title: field.title,
                        required: quantity > 0 && field.required,
                        values: {
                            ...reduce(
                                repeatIndex(quantity),
                                (acc, _, index) => ({
                                    ...acc,
                                    [index]: field.defaultValue,
                                }),
                                {},
                            ),
                        },
                    });
                }
            }
            if (isProductFormFieldSelect(field)) {
                if (field.defaultValues[0]) {
                    // Set default value of text field
                    // TODO! WE DO NOT SUPPORT MULTI SELECT YET
                    const quantity = calculateProductFormFieldQuantity(
                        field,
                        orderItems,
                    );
                    return productFormSelectAnswerFactory({
                        _id: field._id,
                        options: field.options,
                        minRequired: field.minRequired,
                        maxRequired: field.maxRequired,
                        groupedBy: getGroupName(field.quantity),
                        quantity,
                        title: field.title,
                        required: quantity > 0 && field.required,
                        values: {
                            ...reduce(
                                repeatIndex(quantity),
                                (acc, _, index) => ({
                                    ...acc,
                                    [index]: field.defaultValues[0],
                                }),
                                {},
                            ),
                        },
                    });
                }
            }

            // Set default value to an unfilled answer
            // This is used for fields that are not filled in yet
            const quantity = calculateProductFormFieldQuantity(
                field,
                orderItems,
            );
            return {
                _id: field._id,
                _type: "PRODUCT_FORM_FIELD_ANSWER",
                type: "UNFILLED",
                originalType: field.type,
                required: quantity > 0 && field.required,
            } satisfies Cart_ProductFormUnfilledFieldAnswer;
        }),
    });

    // Register form in cart context if there is one
    useRegisterProductFormInCart(productForm);

    const [form] = useForm3({
        formId: `form_for__${productForm.productId}` as unknown as FormId,
        initializeIfNotExists: {
            clearOnUnmount: true,
            schema: cart__productFormAnswerWorkingCopyZodObject,
            initialData: workingCopyRef.current,
        },
        afterUnmount: () => {
            // Clear answers if it's an old cart
            // and there were leftovers
            browserLogger.log("Form unmounted; clearing answers");
            clearProductFormAnswers();
        },
    });

    const safeParse = form.safeParse;
    const registerProductFormAnswers = props.registerProductFormAnswers;
    // If onFormChange is provided, the function will be called whenever
    // the form data changes
    useEffect(
        function formChange() {
            // If there are no fields, there is no product form
            if (!hasForm) return;
            startTransition(() => {
                workingCopyRef.current = form.data;
                const parsedWorkingCopy = safeParse();
                const parsed = cart__productFormAnswerZodObject.safeParse({
                    ...parsedWorkingCopy.data,
                    answers: (parsedWorkingCopy.data?.answers
                        .filter(item => item.type !== "UNFILLED")
                        .filter(exists) ??
                        []) satisfies Cart_ProductFormAnswer["answers"],
                });
                browserLogger.log("FORM CHANGE", {
                    parsed,
                    parsedWorkingCopy,
                    formData: form.data,
                });
                setValid(parsed.success);

                // What to do with the form data?
                // Store it in cart context probably..
                registerProductFormAnswers({
                    productFormId: productForm._id,
                    result: parsed.success ? parsed.data : null,
                    errorMessage: parsed.success ? null : parsed.error.message,
                });
            });
        },
        [
            safeParse,
            form.data,
            productForm._id,
            props,
            registerProductFormAnswers,
            productForm.fields.length,
            hasForm,
        ],
    );

    const setFieldAnswer = (
        fieldId: ProductFormFieldId,
        value: Cart_ProductFormAnswer["answers"][number],
        /**
         * Since values because of a fix,
         * values are not set in one form and need to be
         * merged, forceExact forces this function to ignore
         * existing values and use the values provided only
         * */
        forcedExact: boolean = false,
    ) => {
        const currentFieldAnswers = form.data.answers ?? [];
        const answerIndex = currentFieldAnswers.findIndex(
            f => f._id === fieldId,
        );
        const currentFieldAnswer = currentFieldAnswers[answerIndex];

        const updatedFieldAnswer = {
            ...value,
            values: forcedExact
                ? value.values
                : hasProp(currentFieldAnswer, "values")
                  ? {
                        ...currentFieldAnswer?.values,
                        ...value.values,
                    }
                  : value.values,
        } as typeof value;

        // If the field already exists, update it
        // else add it to the bottom of the list
        browserLogger.log("SET FIELD RESULT", {
            fieldId,
            value,
            currentAnswers: currentFieldAnswers,
            answerIndex,
            currentAnswer: currentFieldAnswer,
            updatedAnswer: updatedFieldAnswer,
        });

        const updatedFieldAnswers = currentFieldAnswers.map(item => {
            if (item._id === fieldId) {
                return updatedFieldAnswer;
            }
            return item;
        }) as Cart_FieldAnswer[];

        const output = updatedFieldAnswers.map(answer => {
            const productFormField = productForm.fields.find(
                field => field._id === answer._id,
            );
            if (!productFormField) throw new Error("Field not found");
            const quantity = calculateProductFormFieldQuantity(
                productFormField,
                orderItems,
            );
            const result = Array.from(
                fixQuantity({
                    answers: [answer],
                    quantity,
                }),
            )[0];
            if (!result) throw new Error("Result could not be calculated");
            return result;
        });

        browserLogger.log("SET FIELD RESULT UPDATE", {
            fieldId,
            currentAnswer: currentFieldAnswer,
            updatedAnswer: updatedFieldAnswer,
            updatedFieldAnswers,
            output,
        });

        // Update the form
        form.setFormField("answers", output);
    };

    // Prevent setFieldAnswer from triggering useEffect below
    const setFieldAnswerRef = useRef(setFieldAnswer);
    setFieldAnswerRef.current = setFieldAnswer;

    /**
     * A generator function that ensures the quantity of answers matches the specified correct quantity.
     * If the quantity of an answer is incorrect, it updates the answer's values to reflect the correct quantity.
     *
     * @param params - The parameters for the function.
     * @param params.answers - An array of `Cart_FieldAnswer` objects to be processed.
     * @param params.quantity - The correct quantity that each answer should have.
     *
     * @yields `Cart_FieldAnswer` - The processed `Cart_FieldAnswer` objects, either unchanged or updated to match the correct quantity.
     *
     * @remarks
     * - If an answer's type is "UNFILLED", it is yielded as-is.
     * - If the quantity of an answer does not match the correct quantity, the `values` property is updated to reflect the correct quantity.
     * - Logs the changes made to the form fields for debugging purposes.
     */
    const fixQuantity = useCallback(function* (params: {
        answers: Cart_FieldAnswer[];
        quantity: number;
    }): Generator<Cart_FieldAnswer> {
        const answers = params.answers;
        const correctQuantity = params.quantity;

        for (let index = 0; index < answers.length; index++) {
            const answer = answers[index];
            if (answer.type === "UNFILLED") {
                yield answer;
                continue;
            }
            if (answer.quantity !== correctQuantity) {
                // If the quantity has changed, update the form
                const indexes = Object.keys(answer.values).map(Number);
                const currentMaxIndex = maxOf(indexes);
                const newMaxIndex = Math.max(0, correctQuantity - 1);
                const updatedValues = {
                    ...answer.values,
                };
                if (newMaxIndex < currentMaxIndex) {
                    for (let i = newMaxIndex + 1; i <= currentMaxIndex; i++) {
                        delete updatedValues[i];
                    }
                }
                browserLogger.log("SET FORM FIELDS VALUES " + index, {
                    currentValues: answer.values,
                    updatedValues,
                    correctQuantity,
                    currentMaxIndex,
                    newMaxIndex,
                    currentQuantity: answer.quantity,
                });

                const updatedAnswer = {
                    ...answer,
                    quantity: correctQuantity,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    values: updatedValues as any,
                } satisfies Cart_FieldAnswer;

                yield updatedAnswer;
            } else {
                // If the quantity has not changed, yield the answer
                yield answer;
            }
        }
    }, []);

    const clearNumberOfAnswers = useCallback(
        (params: {
            fields: ProductFormField[];
            answers: Cart_FieldAnswer[];
            action: (
                fieldId: ProductFormFieldId,
                update: Cart_FieldAnswer,
            ) => void;
            orderItems: OrderItem[];
        }): void => {
            const answers = params.answers;
            const action = params.action;
            const fields = params.fields;
            const orderItems = params.orderItems;

            for (const field of fields) {
                // for each field answer in the form,
                // check if quantity has changed

                // Calculate the correct quantity
                const correctQuantity = calculateProductFormFieldQuantity(
                    field,
                    orderItems,
                );

                // Select only answers for the field
                const fieldAnswers = answers.filter(
                    item => item._id === field._id,
                );

                Array.from(
                    // Iterate all answers
                    fixQuantity({
                        answers: fieldAnswers,
                        quantity: correctQuantity,
                    }),
                ).forEach(answer => {
                    // And run the action
                    action(field._id, answer);
                });
            }
        },
        [fixQuantity],
    );

    useEffect(() => {
        // When order items and their quantity changes, we need to update the form
        clearNumberOfAnswers({
            fields: productForm.fields,
            answers: workingCopyRef.current?.answers ?? [],
            orderItems,
            action: (
                formFieldId: ProductFormFieldId,
                answer: Cart_FieldAnswer,
            ) => {
                setFieldAnswerRef.current(
                    formFieldId,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    answer as any,
                    true,
                );
            },
        });
    }, [orderItems, productForm.fields, clearNumberOfAnswers]);

    const groupedFields = useMemo(
        () => productFormFieldUtils.group(productForm.fields),
        [productForm.fields],
    );

    return (
        <ProductContextProvider product={product}>
            <FormValidContext.Provider value={valid}>
                <FormIdProvider formId={productForm._id as unknown as FormId}>
                    <FlexColumn
                        fillParentWidth
                        crossAxis="stretch"
                        style={{
                            backgroundColor: valid
                                ? theme.colors.default.background.asString
                                : theme.isLightTheme
                                  ? theme.colors.warning.light.light95.asString
                                  : theme.colors.warning.light.light10.asString,
                        }}
                        css={{
                            flex: 1,
                            transition: "background-color 0.3s ease-in-out",
                        }}>
                        {/* 
                        TODO! 
                        Make this conditional true only when there are more
                        than one form for the cart 
                    */}
                        <Conditional when={true}>
                            <FlexColumn
                                css={{ paddingLeft: 20, paddingRight: 20 }}>
                                <VerticalDivider M />
                                <TextBox
                                    text={product.meta.title}
                                    weight="700"
                                    size={1.1}
                                />
                                <VerticalDivider XXS />
                                <TextBox
                                    text={{
                                        sv: `Fyll i formuläret`,
                                        en: `Fill in the form`,
                                    }}
                                    size={0.825}
                                    color={theme.colors.black.light60}
                                />
                            </FlexColumn>
                        </Conditional>
                        <ForEach
                            itemOf={groupedFields}
                            divider={<VerticalDivider M />}
                            beginWith={<VerticalDivider M />}
                            endWith={<VerticalDivider M />}>
                            {fields => {
                                return (
                                    <CartProductForm_FieldGroup
                                        fields={fields}
                                        orderItems={orderItems}
                                        onChange={(
                                                fieldId: ProductFormFieldId,
                                            ) =>
                                            (value: ProductFormFieldAnswer) => {
                                                setFieldAnswer(fieldId, value);
                                            }}
                                    />
                                );
                            }}
                        </ForEach>
                    </FlexColumn>
                </FormIdProvider>
            </FormValidContext.Provider>
        </ProductContextProvider>
    );
};

const CartProductForm_FieldGroup = (props: {
    readonly fields: ProductFormField | ProductFormField[];
    readonly orderItems: OrderItem[];
    readonly onChange: (
        fieldId: ProductFormFieldId,
    ) => (value: ProductFormFieldAnswer) => void;
    readonly className?: string;
}) => {
    const theme = useTheme();
    const product = useProductFromContext();
    const isEmpty = useIsTranslatableEmpty();
    const isNotEmpty = useIsTranslatableNotEmpty();
    const fields = useMemo(() => asArray(props.fields), [props.fields]);
    const quantity = useMemo(
        () => calculateProductFormFieldQuantity(fields[0], props.orderItems),
        [fields, props.orderItems],
    );
    const moreThanOneInGroup = fields.length > 1;
    const moreThanOneInQuantity = quantity > 1;
    const fieldGroupType = fields[0].quantity;
    const isDynamicQuantity = typeof fieldGroupType !== "number";
    const isStaticQuantity = !isDynamicQuantity;

    const groupDescription = !moreThanOneInGroup
        ? fields[0].description
        : undefined;

    const showGroupTitle = (() => {
        if (fieldGroupType === "ITEMS_IN_CART" && quantity === 1) {
            return false;
        }
        if (
            !moreThanOneInGroup &&
            !moreThanOneInQuantity &&
            isProductFormFieldCheckbox(fields[0])
        ) {
            return false;
        }

        return moreThanOneInQuantity || moreThanOneInGroup || isStaticQuantity;
    })();

    return (
        <FlexColumn
            fillParentWidth
            crossAxis="stretch"
            css={{ flex: 1, label: "field_group" }}>
            <Conditional when={showGroupTitle}>
                <FlexColumn css={{ paddingLeft: 20, paddingRight: 20 }}>
                    <FieldTitle
                        text={
                            !moreThanOneInGroup
                                ? joinTranslatables([
                                      fields[0].title,
                                      ...(quantity > 1
                                          ? ` (x${quantity})`
                                          : ""),
                                  ])
                                : ((): RequiredTranslatable => {
                                      if (isStaticQuantity) {
                                          return joinTranslatables([
                                              fields[0].title,
                                              ...(quantity > 1
                                                  ? ` (x${quantity})`
                                                  : ""),
                                          ]);
                                      }
                                      if (fieldGroupType === "ITEMS_IN_CART") {
                                          return joinTranslatables([
                                              translation({
                                                  sv: "Per ",
                                                  en: "Per ",
                                              }),
                                              textUtils.changeCase(
                                                  product.meta.title,
                                                  "lower",
                                              ),
                                              ...(quantity > 1
                                                  ? ` (x${quantity})`
                                                  : ""),
                                          ]);
                                      }
                                      if (
                                          isAccommodationQuantityType(
                                              fieldGroupType,
                                          )
                                      ) {
                                          return joinTranslatables([
                                              accommodationQuantityTypeMeta[
                                                  fieldGroupType
                                              ].name.plural,
                                              ` (x${quantity})`,
                                          ]);
                                      }
                                      if (isBookableOptionId(fieldGroupType)) {
                                          const sku = product.skus[0];
                                          if (isBookable(sku.acquire)) {
                                              const bookableOptions =
                                                  getBookableResourceReferences(
                                                      sku.acquire,
                                                  ).flatMap(rr => rr.options);
                                              const title =
                                                  bookableOptions.find(
                                                      item =>
                                                          item._id ===
                                                          fieldGroupType,
                                                  )?.meta.title;
                                              if (isNotEmpty(title)) {
                                                  return joinTranslatables([
                                                      title,
                                                      ` (x${quantity})`,
                                                  ]);
                                              }
                                          }
                                      }

                                      browserLogger.error(
                                          "Unknown field group type",
                                          {
                                              fieldGroupType,
                                              productId: product._id,
                                          },
                                      );

                                      return `Fält (x${quantity})`;
                                  })()
                        }
                    />
                    {isEmpty(groupDescription) ? null : (
                        <>
                            <VerticalDivider XS />
                            <TextBox
                                text={groupDescription}
                                color={theme.colors.black.light60}
                                size={0.825}
                            />
                        </>
                    )}
                </FlexColumn>
            </Conditional>
            <FlexRow>
                <FlexColumn crossAxis="stretch" css={{ flex: 1 }}>
                    <ForEach
                        itemOf={repeatIndex(quantity)}
                        divider={
                            moreThanOneInGroup ? (
                                <VerticalDivider M />
                            ) : (
                                <VerticalDivider XS />
                            )
                        }>
                        {index => {
                            const isFirst = index === 0;
                            const hideTitle = true;
                            const hideDescription =
                                moreThanOneInQuantity && index !== 0;
                            // !moreThanOneInGroup;

                            return (
                                <>
                                    <Conditional
                                        when={
                                            showGroupTitle &&
                                            isFirst &&
                                            hideTitle
                                        }>
                                        <VerticalDivider S />
                                    </Conditional>
                                    <Conditional
                                        when={
                                            showGroupTitle &&
                                            isFirst &&
                                            !hideTitle
                                        }>
                                        <VerticalDivider S />
                                    </Conditional>
                                    <FlexRow
                                        css={{
                                            flex: 1,
                                            paddingLeft: 20,
                                            paddingRight: 20,
                                        }}
                                        crossAxis={
                                            moreThanOneInGroup
                                                ? "flex-start"
                                                : "center"
                                        }>
                                        <Conditional
                                            when={moreThanOneInQuantity}>
                                            <FlexRow
                                                css={{
                                                    paddingTop:
                                                        moreThanOneInGroup
                                                            ? 5
                                                            : 0,
                                                }}>
                                                <_IndexCircle
                                                    index={index + 1}
                                                />
                                                <HorizontalDivider S />
                                            </FlexRow>
                                        </Conditional>
                                        <FlexColumn css={{ flex: 1 }}>
                                            <ForEach
                                                itemOf={fields}
                                                divider={
                                                    <VerticalDivider XS />
                                                }>
                                                {field => {
                                                    return (
                                                        <FlexColumn
                                                            crossAxis="stretch"
                                                            css={{ flex: 1 }}>
                                                            {/* {JSON.stringify({
                                                                // hideDescription,
                                                                mg: moreThanOneInGroup,
                                                                mq: moreThanOneInQuantity,
                                                            })} */}
                                                            <CartProductForm_SingleField
                                                                field={field}
                                                                onChange={props.onChange(
                                                                    field._id,
                                                                )}
                                                                hideTitle={
                                                                    hideTitle
                                                                }
                                                                hideDescription={
                                                                    hideDescription ||
                                                                    (!moreThanOneInGroup &&
                                                                        ((field.type ===
                                                                            "CHECKBOX" &&
                                                                            moreThanOneInQuantity) ||
                                                                            field.type !==
                                                                                "CHECKBOX"))
                                                                }
                                                                className={
                                                                    props.className
                                                                }
                                                                index={index}
                                                            />
                                                        </FlexColumn>
                                                    );
                                                }}
                                            </ForEach>
                                        </FlexColumn>
                                    </FlexRow>
                                </>
                            );
                        }}
                    </ForEach>
                </FlexColumn>
            </FlexRow>
        </FlexColumn>
    );
};

const CartProductForm_SingleField = (props: {
    readonly field: ProductFormField;
    readonly onChange: (value: ProductFormFieldAnswer) => void;
    readonly className?: string;
    readonly index: number;
    readonly hideTitle?: boolean;
    readonly hideDescription?: boolean;
}) => {
    const field = props.field;
    if (isProductFormFieldCheckbox(field)) {
        return (
            <CartProductForm_Checkbox
                field={field}
                index={props.index}
                className={props.className}
                hideDescription={props.hideDescription}
                onChange={
                    props.onChange as (
                        value: ProductFormField_CheckboxAnswer,
                    ) => void
                }
            />
        );
    }
    if (isProductFormFieldText(field)) {
        return (
            <CartProductForm_Text
                field={field}
                index={props.index}
                className={props.className}
                hideTitle={props.hideTitle}
                hideDescription={props.hideDescription}
                onChange={
                    props.onChange as (
                        value: ProductFormField_TextAnswer,
                    ) => void
                }
            />
        );
    }
    if (isProductFormFieldSelect(field)) {
        return (
            <CartProductForm_Select
                field={field}
                index={props.index}
                className={props.className}
                hideTitle={props.hideTitle}
                hideDescription={props.hideDescription}
                onChange={
                    props.onChange as (
                        value: ProductFormField_SelectAnswer,
                    ) => void
                }
            />
        );
    }
    assertNever(field);
};

const CartProductForm_Checkbox = (props: {
    readonly field: ProductFormField_Checkbox;
    readonly onChange: (value: ProductFormField_CheckboxAnswer) => void;
    readonly className?: string;
    readonly index: number;
    readonly hideDescription?: boolean;
}) => {
    const theme = useTheme();
    const isEmpty = useIsTranslatableEmpty();
    const field = props.field;
    const orderItems = useCartContext(context => context.cart.orderItems);
    const quantity = useMemo(
        () => calculateProductFormFieldQuantity(field, orderItems),
        [field, orderItems],
    );

    return (
        <Form3
            onFormChange={(value: Partial<ProductFormField_CheckboxAnswer>) => {
                props.onChange(
                    value as unknown as ProductFormField_CheckboxAnswer,
                );
            }}
            initializeIfNotExists={{
                clearOnUnmount: true,
                schema: productFormCheckboxAnswerZodObject,
                initialData: productFormCheckboxAnswerFactory({
                    _id: field._id,
                    title: field.title,
                    required: quantity > 0 && field.required,
                    quantity: quantity,
                    groupedBy: getGroupName(field.quantity),
                    values: {
                        [props.index]: field.defaultValue ?? false,
                    },
                }),
            }}>
            {({ FormComponents }) => {
                return (
                    <FlexColumn
                        className={props.className}
                        style={{
                            flex: 1,
                            overflowX: "hidden",
                        }}>
                        <FormComponents.Checkbox
                            key={props.index}
                            fieldId={`values.${props.index}`}
                            text={field.title}
                            showAsRequired={field.required}
                        />
                        <Conditional
                            when={
                                !isEmpty(field.description) &&
                                !props.hideDescription
                            }>
                            <VerticalDivider size={0.3} />
                            <TextBox
                                text={field.description}
                                size={0.8}
                                css={{ paddingLeft: 3, paddingBottom: 5 }}
                                color={theme.colors.black.light60}
                            />
                        </Conditional>
                    </FlexColumn>
                );
            }}
        </Form3>
    );
};

const CartProductForm_Text = (props: {
    readonly field: ProductFormField_Text;
    readonly onChange: (value: ProductFormField_TextAnswer) => void;
    readonly className?: string;
    readonly index: number;
    readonly hideTitle?: boolean;
    readonly hideDescription?: boolean;
}) => {
    const theme = useTheme();
    const valid = useFormValidFromContext();
    const isEmpty = useIsTranslatableEmpty();
    const field = props.field;
    const orderItems = useCartContext(context => context.cart.orderItems);
    const quantity = useMemo(
        () => calculateProductFormFieldQuantity(field, orderItems),
        [field, orderItems],
    );

    return (
        <Form3
            onFormChange={(value: Partial<ProductFormField_TextAnswer>) => {
                props.onChange(value as unknown as ProductFormField_TextAnswer);
            }}
            initializeIfNotExists={{
                clearOnUnmount: true,
                schema: productFormTextAnswerZodObject,
                initialData: productFormTextAnswerFactory({
                    _id: field._id,
                    title: field.title,
                    required: quantity > 0 && field.required,
                    quantity,
                    groupedBy: getGroupName(field.quantity),
                    values: {
                        [props.index]: field.defaultValue ?? "",
                    },
                }),
            }}>
            {({ FormComponents }) => {
                return (
                    <FlexColumn
                        css={{ flex: 1 }}
                        crossAxis="stretch"
                        className={props.className}>
                        <FlexColumn crossAxis="stretch" css={{ flex: 1 }}>
                            <FlexRow crossAxis="center">
                                <FormComponents.TextInput
                                    key={props.index}
                                    fieldId={`values.${props.index}`}
                                    colorSet={
                                        valid ? undefined : theme.colors.white
                                    }
                                    placeholder={field.title}
                                    showAsRequired={field.required}
                                    css={{ flex: 1 }}
                                    label={
                                        !props.hideTitle
                                            ? field.title
                                            : undefined
                                    }
                                />
                            </FlexRow>
                        </FlexColumn>
                        <Conditional
                            when={
                                !isEmpty(field.description) &&
                                !props.hideDescription
                            }>
                            <VerticalDivider size={0.3} />
                            <TextBox
                                text={field.description}
                                size={0.8}
                                css={{ paddingLeft: 3, paddingBottom: 5 }}
                                color={theme.colors.black.light60}
                            />
                        </Conditional>
                    </FlexColumn>
                );
            }}
        </Form3>
    );
};

const CartProductForm_Select = (props: {
    readonly field: ProductFormField_Select;
    readonly onChange: (value: ProductFormField_SelectAnswer) => void;
    readonly className?: string;
    readonly index: number;
    readonly hideTitle?: boolean;
    readonly hideDescription?: boolean;
}) => {
    const theme = useTheme();
    const valid = useFormValidFromContext();
    const isEmpty = useIsTranslatableEmpty();
    const field = props.field;
    const orderItems = useCartContext(context => context.cart.orderItems);
    const quantity = useMemo(
        () => calculateProductFormFieldQuantity(field, orderItems),
        [field, orderItems],
    );

    return (
        <Form3
            onFormChange={(value: Partial<ProductFormField_SelectAnswer>) => {
                props.onChange(
                    value as unknown as ProductFormField_SelectAnswer,
                );
            }}
            initializeIfNotExists={{
                clearOnUnmount: true,
                schema: productFormSelectAnswerZodObject,
                initialData: productFormSelectAnswerFactory({
                    _id: field._id,
                    title: field.title,
                    required: quantity > 0 && field.minRequired > 0,
                    minRequired: field.minRequired,
                    maxRequired: field.maxRequired,
                    options: field.options,
                    quantity,
                    groupedBy: getGroupName(field.quantity),
                    values: {
                        [props.index]: field.defaultValues?.[0] ?? "",
                    },
                }),
            }}>
            {({ FormComponents }) => {
                return (
                    <FlexColumn className={props.className}>
                        <FormComponents.Select
                            key={props.index}
                            fieldId={`values.${props.index}`}
                            options={field.options}
                            placeholder={field.title}
                            label={!props.hideTitle ? field.title : undefined}
                            description={
                                !isEmpty(field.description) &&
                                !props.hideDescription
                                    ? field.description
                                    : undefined
                            }
                            colorSet={valid ? undefined : theme.colors.white}
                            showAsRequired={field.required}
                        />
                    </FlexColumn>
                );
            }}
        </Form3>
    );
};

const _IndexCircle = (props: { readonly index: number }) => {
    const theme = useTheme();
    const index = props.index;
    const valid = useFormValidFromContext();

    return (
        <FlexRow
            mainAxis="center"
            crossAxis="center"
            style={{
                borderColor: valid
                    ? theme.colors.textInput.border.asString
                    : theme.colors.default.border.asString,
                backgroundColor: valid
                    ? theme.colors.textInput.background.asString
                    : theme.colors.default.background.asString,
                color: valid
                    ? theme.colors.textInput.text.asString
                    : theme.colors.default.text.asString,
            }}
            css={{
                borderRadius: 3000,
                borderStyle: "solid",
                borderWidth: 1,
                width: "2rem",
                height: "2rem",
                color: theme.colors.textInput.text.asString,
                transition: "background-color 0.5s ease-in-out",
            }}>
            <NumberTextBox value={index} />
        </FlexRow>
    );
};

export { ProductForms_Cart };
